import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { SnapshotCompare } from "./SnapshotCompare";
import { 
  FileSearch,
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Upload,
  Search,
  FileText,
  Image,
  AlertCircle,
  XCircle,
  CheckCircle2,
  Download,
  Folder
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bucket color helper
const getBucketColor = (bucket) => {
  switch (bucket) {
    case 1: return "text-green-500 bg-green-900/30";
    case 2: return "text-blue-500 bg-blue-900/30";
    case 3: return "text-amber-500 bg-amber-900/30";
    case 4: return "text-orange-500 bg-orange-900/30";
    case 5: return "text-red-500 bg-red-900/30";
    default: return "text-zinc-500 bg-zinc-800";
  }
};

const getBucketLabel = (bucket) => {
  switch (bucket) {
    case 1: return "0-5%";
    case 2: return "6-15%";
    case 3: return "16-35%";
    case 4: return "36-60%";
    case 5: return "61%+";
    default: return "-";
  }
};

// Site Card Component
const SiteCard = ({ site, onScan, onDelete, onSelect, onRescanErrors, isSelected, errorCount, onImportUrls }) => {
  const getStatusIcon = () => {
    switch (site.status) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "SCANNING":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "ERROR":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };

  const progress = site.total_pages > 0 
    ? Math.round((site.scanned_pages / site.total_pages) * 100) 
    : 0;

  return (
    <Card 
      className={cn(
        "bg-zinc-900 border rounded-sm cursor-pointer transition-all",
        isSelected ? "border-green-500 ring-1 ring-green-500/50" : "border-zinc-800 hover:border-zinc-600"
      )}
      onClick={() => onSelect(site)}
      data-testid={`seo-site-card-${site.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Globe className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-mono text-sm text-white truncate">{site.name}</p>
              <p className="text-xs text-zinc-500 truncate">{site.domain}</p>
            </div>
          </div>
          {getStatusIcon()}
        </div>

        {site.status === "SCANNING" && (
          <div className="mb-3">
            <Progress value={progress} className="h-1" />
            <p className="text-xs text-zinc-500 font-mono mt-1">
              {site.scanned_pages} / {site.total_pages} pages
              {site.robots_blocked_count > 0 && (
                <span className="text-zinc-600 ml-1">({site.robots_blocked_count} blocked by robots.txt)</span>
              )}
            </p>
          </div>
        )}

        {site.status === "ERROR" && (
          <div className="mb-3">
            <p className="text-[10px] text-red-400/80 font-mono mb-1.5 line-clamp-2">{site.error_message || "Scan failed"}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onImportUrls && onImportUrls(site.id);
              }}
              className="w-full text-[10px] font-mono px-2 py-1.5 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/30 rounded-sm transition-colors"
              data-testid={`import-urls-btn-${site.id}`}
            >
              Import URLs Manually
            </button>
          </div>
        )}

        {site.status !== "SCANNING" && (site.meta_issue_percent !== undefined || site.alt_issue_percent !== undefined) && (
          <>
            {/* Meta Issues */}
            <div className="flex items-center justify-between mb-2 p-2 bg-zinc-800/50 rounded-sm">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-zinc-400" />
                <span className="text-xs text-zinc-400 font-mono">Meta</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn("text-sm font-mono font-bold", 
                  site.meta_issue_percent > 35 ? "text-red-500" : 
                  site.meta_issue_percent > 15 ? "text-amber-500" : "text-green-500"
                )}>
                  {site.meta_issue_percent}%
                </span>
                <span className="text-xs text-zinc-500 font-mono">
                  ({site.pages_missing_any_meta}/{site.total_pages})
                </span>
              </div>
            </div>

            {/* Alt Issues */}
            <div className="flex items-center justify-between mb-3 p-2 bg-zinc-800/50 rounded-sm">
              <div className="flex items-center gap-2">
                <Image className="w-4 h-4 text-zinc-400" />
                <span className="text-xs text-zinc-400 font-mono">Alt</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn("text-sm font-mono font-bold",
                  site.alt_issue_percent > 35 ? "text-red-500" : 
                  site.alt_issue_percent > 15 ? "text-amber-500" : "text-green-500"
                )}>
                  {site.alt_issue_percent}%
                </span>
                <span className="text-xs text-zinc-500 font-mono">
                  ({site.images_missing_alt}/{site.total_images})
                </span>
              </div>
            </div>

            {/* Bucket indicators */}
            <div className="flex gap-1 mb-3">
              <div className={cn("flex-1 text-center py-1 rounded-sm text-[10px] font-mono", getBucketColor(site.meta_bucket))}>
                Meta: B{site.meta_bucket}
              </div>
              <div className={cn("flex-1 text-center py-1 rounded-sm text-[10px] font-mono", getBucketColor(site.alt_bucket))}>
                Alt: B{site.alt_bucket}
              </div>
            </div>
          </>
        )}

        {site.last_scan_at && (
          <p className="text-[10px] text-zinc-600 font-mono mb-2">
            Last: {new Date(site.last_scan_at).toLocaleString()}
          </p>
        )}

        <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
          <SnapshotCompare 
            siteId={site.id} 
            siteName={site.name} 
            monitorType="seo"
            buttonClassName="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7 border-zinc-700"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onScan(site.id, false)}
            disabled={site.status === "SCANNING"}
            className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7"
            title="Scan new pages only"
          >
            <RefreshCw className={cn("w-3 h-3 mr-1", site.status === "SCANNING" && "animate-spin")} />
            New
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onScan(site.id, true)}
            disabled={site.status === "SCANNING"}
            className="flex-1 text-blue-400 hover:text-blue-300 hover:bg-blue-950/30 rounded-sm font-mono text-[10px] h-7"
            title="Full rescan all pages"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Full
          </Button>
          {errorCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRescanErrors(site.id)}
              disabled={site.status === "SCANNING"}
              className="flex-1 text-amber-400 hover:text-amber-300 hover:bg-amber-950/30 rounded-sm font-mono text-[10px] h-7"
              title={`Rescan ${errorCount} error pages`}
            >
              <AlertTriangle className="w-3 h-3 mr-1" />
              Retry ({errorCount})
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(site.id)}
            className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm font-mono text-[10px] h-7 px-2"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Page Row Component
const PageRow = ({ page }) => {
  const [expanded, setExpanded] = useState(false);

  const hasTitleIssue = !page.has_title;
  const hasDescIssue = !page.has_description;
  const hasAltIssue = page.missing_alt_images > 0;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
      <div 
        className="p-3 flex items-center justify-between cursor-pointer hover:bg-zinc-800/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {page.scan_error ? (
            <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          ) : (hasTitleIssue || hasDescIssue || hasAltIssue) ? (
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
          )}
          <a 
            href={page.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-zinc-300 hover:text-white truncate font-mono"
            onClick={(e) => e.stopPropagation()}
          >
            {page.url}
          </a>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Meta badge */}
          <div className={cn(
            "px-2 py-0.5 rounded text-[10px] font-mono",
            hasTitleIssue || hasDescIssue ? "bg-amber-900/50 text-amber-400" : "bg-green-900/50 text-green-400"
          )}>
            Meta {hasTitleIssue || hasDescIssue ? "!" : "✓"}
          </div>
          
          {/* Alt badge */}
          <div className={cn(
            "px-2 py-0.5 rounded text-[10px] font-mono",
            hasAltIssue ? "bg-amber-900/50 text-amber-400" : "bg-green-900/50 text-green-400"
          )}>
            Alt {hasAltIssue ? `${page.missing_alt_images}/${page.total_images}` : "✓"}
          </div>
          
          <a
            href={page.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-zinc-500 hover:text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-zinc-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-500" />
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="px-3 pb-3 border-t border-zinc-800 pt-3 space-y-3">
          {page.scan_error ? (
            <p className="text-xs text-red-500 font-mono">Error: {page.error_message}</p>
          ) : (
            <>
              {/* Meta details */}
              <div className="space-y-2">
                <p className="text-xs text-zinc-400 font-mono font-bold">Meta Tags:</p>
                <div className="flex items-start gap-2">
                  {page.has_title ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <span className="text-xs text-zinc-400 font-mono">Title: </span>
                    <span className={cn("text-xs font-mono", page.has_title ? "text-zinc-300" : "text-red-400")}>
                      {page.has_title ? (page.title_content || "Present") : "MISSING"}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  {page.has_description ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <span className="text-xs text-zinc-400 font-mono">Description: </span>
                    <span className={cn("text-xs font-mono", page.has_description ? "text-zinc-300" : "text-red-400")}>
                      {page.has_description ? (page.description_content?.substring(0, 100) + "..." || "Present") : "MISSING"}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Image details */}
              <div className="space-y-2">
                <p className="text-xs text-zinc-400 font-mono font-bold">
                  Images: {page.missing_alt_images} missing alt out of {page.total_images}
                </p>
                {page.images_with_missing_alt && page.images_with_missing_alt.length > 0 && (
                  <div className="text-xs text-zinc-500 font-mono space-y-1 max-h-32 overflow-y-auto">
                    {page.images_with_missing_alt.slice(0, 5).map((img, idx) => (
                      <p key={idx} className="truncate text-red-400">• {img}</p>
                    ))}
                    {page.images_with_missing_alt.length > 5 && (
                      <p className="text-zinc-600">+{page.images_with_missing_alt.length - 5} more</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Main SEO Monitor Component
export const SEOMonitor = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [siteErrorCounts, setSiteErrorCounts] = useState({});
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [siteName, setSiteName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Manual URL import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSiteId, setImportSiteId] = useState(null);
  const [importUrls, setImportUrls] = useState("");
  const [isImportingUrls, setIsImportingUrls] = useState(false);
  const [pageFilter, setPageFilter] = useState("all");
  const [selectedSitemapType, setSelectedSitemapType] = useState("all");
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("all");

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
    } catch (e) {
      console.error("Failed to fetch groups:", e);
    }
  }, []);

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const filteredSites = sites.filter((site) => {
    // Group filter
    if (filterGroup !== "all") {
      if (!site.groups || !site.groups.includes(filterGroup)) {
        return false;
      }
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      site.name?.toLowerCase().includes(query) ||
      site.domain?.toLowerCase().includes(query)
    );
  });

  // Fetch error counts for all sites
  const fetchErrorCounts = useCallback(async (siteList) => {
    const counts = {};
    for (const site of siteList) {
      if (site.status === 'COMPLETED') {
        try {
          const response = await axios.get(`${API}/seo-monitor/${site.id}/stats`);
          counts[site.id] = response.data.errors || 0;
        } catch (e) {
          counts[site.id] = 0;
        }
      }
    }
    setSiteErrorCounts(counts);
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/seo-monitor`);
      setSites(response.data);
      
      // Fetch error counts for completed sites
      fetchErrorCounts(response.data);
      
      if (response.data.length > 0 && !selectedSite) {
        setSelectedSite(response.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch sites:", e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSite, fetchErrorCounts]);

  const fetchPages = useCallback(async () => {
    if (!selectedSite) return;
    setIsLoadingPages(true);
    try {
      const params = new URLSearchParams();
      if (pageFilter !== "all") params.append('filter', pageFilter);
      if (selectedSitemapType && selectedSitemapType !== "all") params.append('sitemap_type', selectedSitemapType);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/seo-monitor/${selectedSite.id}/pages${queryString}`);
      setPages(response.data);
    } catch (e) {
      console.error("Failed to fetch pages:", e);
    } finally {
      setIsLoadingPages(false);
    }
  }, [selectedSite, pageFilter, selectedSitemapType]);

  const fetchStats = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const response = await axios.get(`${API}/seo-monitor/${selectedSite.id}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    fetchSites();
    fetchGroups();
    const interval = setInterval(fetchSites, 10000);
    return () => clearInterval(interval);
  }, [fetchSites, fetchGroups]);

  // Reset sitemap type filter only when site changes
  useEffect(() => {
    if (selectedSite) {
      setSelectedSitemapType("all");
    }
  }, [selectedSite?.id]);

  // Fetch pages and stats when site, filter, or sitemap type changes
  useEffect(() => {
    if (selectedSite) {
      fetchPages();
      fetchStats();
    }
  }, [selectedSite?.id, pageFilter, selectedSitemapType, fetchPages, fetchStats]);

  const handleAddSite = async (e) => {
    e.preventDefault();
    if (!sitemapUrl.trim()) return;

    setIsAdding(true);
    try {
      const response = await axios.post(`${API}/seo-monitor`, {
        sitemap_url: sitemapUrl,
        name: siteName || undefined,
        groups: selectedGroups
      });
      toast.success("Site added - scanning started");
      setSitemapUrl("");
      setSiteName("");
      setSelectedGroups([]);
      setSelectedSite(response.data);
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add site");
    } finally {
      setIsAdding(false);
    }
  };

  const handleScan = async (siteId, full = false) => {
    try {
      await axios.post(`${API}/seo-monitor/${siteId}/scan?full=${full}`);
      toast.success(full ? "Full scan started" : "Scan started");
      fetchSites();
    } catch (e) {
      toast.error("Failed to start scan");
    }
  };

  const handleRescanErrors = async (siteId) => {
    try {
      const response = await axios.post(`${API}/seo-monitor/${siteId}/rescan-errors`);
      if (response.data.error_count > 0) {
        toast.success(`Rescanning ${response.data.error_count} error pages (with rate limiting)`);
      } else {
        toast.info("No error pages to rescan");
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to start rescan");
    }
  };

  const handleDelete = async (siteId) => {
    try {
      await axios.delete(`${API}/seo-monitor/${siteId}`);
      toast.success("Site deleted");
      if (selectedSite?.id === siteId) {
        setSelectedSite(null);
        setPages([]);
        setStats(null);
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to delete site");
    }
  };

  const handleImportUrls = async () => {
    if (!importUrls.trim() || !importSiteId) return;
    
    const urls = importUrls.split("\n").map(u => u.trim()).filter(u => u.startsWith("http"));
    if (urls.length === 0) {
      toast.error("No valid URLs found. Each URL must start with http:// or https://");
      return;
    }
    
    setIsImportingUrls(true);
    try {
      const response = await axios.post(`${API}/seo-monitor/${importSiteId}/manual-urls`, { urls });
      toast.success(response.data.message);
      setShowImportModal(false);
      setImportUrls("");
      setImportSiteId(null);
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to import URLs");
    } finally {
      setIsImportingUrls(false);
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/seo-monitor/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { added, skipped, errors } = response.data;
      if (added.length > 0) toast.success(`Imported ${added.length} site(s)`);
      if (skipped.length > 0) toast.info(`Skipped ${skipped.length} duplicate(s)`);
      if (errors.length > 0) toast.error(`${errors.length} error(s)`);
      
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to import CSV");
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const filters = [
    { id: "all", label: "All Pages" },
    { id: "missing_meta", label: "Missing Meta" },
    { id: "missing_alt", label: "Missing Alt" },
    { id: "oversized_images", label: "Oversized Images" },
    { id: "high_priority", label: "High Priority" },
    { id: "errors", label: "Errors" },
  ];

  const handleExportCsv = () => {
    if (!selectedSite) return;
    const filterParam = pageFilter !== "all" ? `?filter=${pageFilter}` : "";
    window.open(`${API}/seo-monitor/${selectedSite.id}/export-csv${filterParam}`, '_blank');
    toast.success("Export started");
  };

  return (
    <div className="text-zinc-100">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#09090b]/80 border-b border-zinc-800">
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <FileSearch className="w-7 h-7 text-purple-500" />
            <div>
              <h1 className="font-mono text-lg font-bold tracking-tight">MetaAlt Monitoring</h1>
              <p className="text-xs text-zinc-500 font-mono">Meta tags & alt text analysis</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6">
        {/* Add Site Form */}
        <section className="mb-6">
          <form onSubmit={handleAddSite} className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="url"
                  placeholder="https://example.com/sitemap.xml"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                  className="pl-11 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm placeholder:text-zinc-600"
                  required
                />
              </div>
              <Input
                type="text"
                placeholder="Site name (optional)"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                className="w-48 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm placeholder:text-zinc-600"
              />
              <Button
                type="submit"
                disabled={isAdding || !sitemapUrl.trim()}
                className="bg-white text-black hover:bg-zinc-200 rounded-sm font-mono text-sm uppercase tracking-wider px-6"
              >
                <Plus className="w-4 h-4 mr-2" />
                {isAdding ? "Adding..." : "Add"}
              </Button>
              <div className="relative">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvImport}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isImporting}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isImporting}
                  className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm font-mono text-sm uppercase tracking-wider"
                >
                  <Upload className={`w-4 h-4 mr-2 ${isImporting ? 'animate-pulse' : ''}`} />
                  {isImporting ? "..." : "CSV"}
                </Button>
              </div>
            </div>
            {/* Group Selection */}
            {groups.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-zinc-500 font-mono">Assign to groups:</span>
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => toggleGroupSelection(group.id)}
                    className={cn(
                      "flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono transition-colors",
                      selectedGroups.includes(group.id)
                        ? "bg-zinc-700 text-white"
                        : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} />
                    {group.name}
                  </button>
                ))}
              </div>
            )}
          </form>
          <p className="text-xs text-zinc-600 font-mono mt-2">
            CSV format: "sitemap_url" column (optionally "name")
          </p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sites List */}
          <div className="lg:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-mono text-sm text-zinc-400">MONITORED SITES</h2>
              <span className="text-xs text-zinc-600 font-mono">{filteredSites.length}/{sites.length}</span>
            </div>
            {/* Search and Group Filter */}
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm placeholder:text-zinc-600 h-9"
                  data-testid="seo-search-input"
                />
              </div>
              {groups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-1 text-xs text-zinc-300 font-mono h-9"
                  data-testid="seo-group-filter"
                >
                  <option value="all">All Groups</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 animate-pulse">
                    <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-800 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : sites.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-zinc-800 rounded-sm">
                <FileSearch className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 font-mono text-sm">No sites monitored</p>
              </div>
            ) : filteredSites.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-zinc-800 rounded-sm">
                <Search className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 font-mono text-sm">No matches found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-350px)] overflow-y-auto pr-1">
                {filteredSites.map((site) => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    onScan={handleScan}
                    onDelete={handleDelete}
                    onSelect={setSelectedSite}
                    onRescanErrors={handleRescanErrors}
                    isSelected={selectedSite?.id === site.id}
                    errorCount={siteErrorCounts[site.id] || 0}
                    onImportUrls={(siteId) => {
                      setImportSiteId(siteId);
                      setImportUrls("");
                      setShowImportModal(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pages Panel */}
          <div className="lg:col-span-2">
            {selectedSite ? (
              <>
                {/* Summary Stats */}
                {stats && (
                  <div className="grid grid-cols-5 gap-3 mb-4">
                    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
                      <p className="text-xl font-mono font-bold text-zinc-100">{stats.total_pages}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">Total Pages</p>
                      {selectedSite?.robots_blocked_count > 0 && (
                        <p className="text-[9px] text-zinc-600 font-mono mt-1">{selectedSite.robots_blocked_count} blocked by robots.txt</p>
                      )}
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
                      <p className={cn("text-xl font-mono font-bold", stats.missing_title > 0 ? "text-amber-500" : "text-green-500")}>
                        {stats.missing_title}
                      </p>
                      <p className="text-[10px] text-zinc-500 font-mono">Missing Title</p>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
                      <p className={cn("text-xl font-mono font-bold", stats.missing_description > 0 ? "text-amber-500" : "text-green-500")}>
                        {stats.missing_description}
                      </p>
                      <p className="text-[10px] text-zinc-500 font-mono">Missing Desc</p>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
                      <p className={cn("text-xl font-mono font-bold", stats.missing_alt > 0 ? "text-amber-500" : "text-green-500")}>
                        {stats.missing_alt}
                      </p>
                      <p className="text-[10px] text-zinc-500 font-mono">Missing Alt</p>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
                      <p className={cn("text-xl font-mono font-bold", (stats.oversized_images || 0) > 0 ? "text-orange-500" : "text-green-500")}>
                        {stats.oversized_images || 0}
                      </p>
                      <p className="text-[10px] text-zinc-500 font-mono">Oversized Img</p>
                    </div>
                  </div>
                )}

                {/* Filter Tabs */}
                <div className="flex gap-1 mb-4 overflow-x-auto items-center">
                  {filters.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setPageFilter(f.id)}
                      className={cn(
                        "px-3 py-1.5 font-mono text-xs rounded-sm transition-colors whitespace-nowrap",
                        pageFilter === f.id 
                          ? "bg-zinc-700 text-white" 
                          : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                      )}
                    >
                      {f.label}
                    </button>
                  ))}
                  <div className="flex-1" />
                  {/* Sitemap Type Filter */}
                  {selectedSite?.sitemap_types && selectedSite.sitemap_types.length > 1 && (
                    <select
                      value={selectedSitemapType}
                      onChange={(e) => setSelectedSitemapType(e.target.value)}
                      className="bg-zinc-900 border border-zinc-700 rounded-sm text-xs font-mono px-2 py-1 text-zinc-300 focus:outline-none focus:border-zinc-500 mr-2"
                      data-testid="sitemap-type-filter"
                    >
                      <option value="all">All Types</option>
                      {selectedSite.sitemap_types.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCsv}
                    disabled={pages.length === 0}
                    className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm font-mono text-xs h-7"
                    data-testid="export-seo-csv-btn"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Export CSV
                  </Button>
                </div>

                {/* Pages List */}
                <div className="space-y-2 max-h-[calc(100vh-420px)] overflow-y-auto pr-1">
                  {isLoadingPages ? (
                    [1, 2, 3].map((i) => (
                      <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 animate-pulse">
                        <div className="h-4 bg-zinc-800 rounded w-3/4" />
                      </div>
                    ))
                  ) : pages.length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-zinc-800 rounded-sm">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-zinc-500 font-mono text-sm">
                        {pageFilter === "all" ? "No pages found" : "No issues in this category"}
                      </p>
                    </div>
                  ) : (
                    pages.map((page) => (
                      <PageRow key={page.id} page={page} />
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 border border-dashed border-zinc-800 rounded-sm">
                <div className="text-center">
                  <FileSearch className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono">Select a site to view SEO analysis</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8">
        <div className="px-6 py-4">
          <p className="text-center text-xs text-zinc-600 font-mono">
            Auto-scan every 24 hours • Buckets: B1 (0-5%) • B2 (6-15%) • B3 (16-35%) • B4 (36-60%) • B5 (61%+)
          </p>
        </div>
      </footer>

      {/* Import URLs Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowImportModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-mono text-sm mb-1">Import URLs Manually</h3>
            <p className="text-zinc-500 text-xs font-mono mb-4">
              Paste one URL per line. Use this when the sitemap can't be fetched automatically (e.g., bot protection).
            </p>
            <textarea
              value={importUrls}
              onChange={(e) => setImportUrls(e.target.value)}
              placeholder={"https://example.com/\nhttps://example.com/about\nhttps://example.com/services"}
              className="w-full h-48 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-mono rounded-sm p-3 mb-3 resize-none focus:outline-none focus:border-zinc-500"
              data-testid="seo-import-urls-textarea"
            />
            <p className="text-zinc-600 text-[10px] font-mono mb-4">
              {importUrls.split("\n").filter(u => u.trim().startsWith("http")).length} valid URLs detected
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowImportModal(false)}
                className="text-zinc-400 hover:text-white font-mono text-xs"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleImportUrls}
                disabled={isImportingUrls || !importUrls.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-mono text-xs"
                data-testid="seo-confirm-import-urls-btn"
              >
                {isImportingUrls ? "Importing..." : "Import & Scan"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOMonitor;
